/**
 * Organization-aware login screen with Claire email and OTP verification.
 */

import { useEffect, useState } from "react";
import loginBg from "@/assets/login-bg.jpeg";
import logo from "@/assets/logo-minimal.png";
import { useApi } from "@/hooks/use-api";
import { Organization } from "@/types/auth";
import { LoginStart } from "./LoginStart";

export const Login = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const organizationCode = searchParams.get("organization");
  const { lookupOrganization } = useApi();

  const [orgDetails, setOrgDetails] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(false);
  const [organizationError, setOrganizationError] = useState<string | null>(null);
  const [isOrgLogoReady, setIsOrgLogoReady] = useState(false);

  useEffect(() => {
    if (organizationCode) {
      setLoading(true);
      setOrganizationError(null);
      setIsOrgLogoReady(false);
      lookupOrganization(organizationCode)
        .then((data) => {
          setOrgDetails(data);
        })
        .catch((error) => {
          console.error(error);
          setOrgDetails(null);
          setOrganizationError("We couldn't find that organization.");
        })
        .finally(() => setLoading(false));
    }
  }, [organizationCode, lookupOrganization]);

  useEffect(() => {
    if (!orgDetails?.logoUrl) {
      setIsOrgLogoReady(false);
      return;
    }

    const image = new Image();
    image.onload = () => setIsOrgLogoReady(true);
    image.onerror = () => setIsOrgLogoReady(false);
    image.src = orgDetails.logoUrl;
  }, [orgDetails?.logoUrl]);

  const shouldShowClaireLogo = !organizationCode || !orgDetails?.logoUrl;
  const shouldShowOrganizationLogo = Boolean(orgDetails?.logoUrl && isOrgLogoReady);
  const organizationLogoUrl = orgDetails?.logoUrl ?? null;
  const organizationName = orgDetails?.name ?? "Organization";

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 bg-white flex flex-col px-6 sm:px-10">
        <div className="flex-1 flex flex-col justify-center max-w-md mx-auto w-full py-10">
          <div className="mb-2 text-center">
            {shouldShowOrganizationLogo ? (
              <img
                src={organizationLogoUrl ?? undefined}
                alt={`${organizationName} logo`}
                className="h-32 w-auto max-w-full mx-auto object-contain"
              />
            ) : shouldShowClaireLogo ? (
              <img src={logo} alt="Claire Logo" className="h-16 w-auto mx-auto" />
            ) : (
              <div className="h-32" aria-hidden="true" />
            )}
          </div>

          {loading ? (
            <div className="text-center mt-10">Loading organization...</div>
          ) : (
            <LoginStart
              organizationId={orgDetails?.id}
              organizationError={organizationError}
            />
          )}
        </div>
      </div>

      <div
        className="hidden lg:block flex-1 min-w-[50%]"
        style={{
          backgroundImage: `url(${loginBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />
    </div>
  );
};
